import { useState, useEffect } from 'react'
import { getAdminProperties, addProperty, updateProperty, deleteProperty, restoreProperty } from '../services/dbService'
import { formatTimestamp } from '../utils/helpers'

export default function AdminProperties() {
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  
  // Form State
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    title: '', type: 'residential', badge: '', img: '', loc: '', area: '', legal: ''
  })
  const [formLoading, setFormLoading] = useState(false)

  const fetchProperties = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAdminProperties()
      setProperties(data)
    } catch (err) {
      setError('Failed to fetch properties. ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProperties()
  }, [])

  const resetForm = () => {
    setFormData({ title: '', type: 'residential', badge: '', img: '', loc: '', area: '', legal: '' })
    setEditingId(null)
    setShowForm(false)
  }

  const handleEdit = (property) => {
    setFormData({
      title: property.title || '',
      type: property.type || 'residential',
      badge: property.badge || '',
      img: property.img || '',
      loc: property.loc || '',
      area: property.area || '',
      legal: property.legal || ''
    })
    setEditingId(property.id)
    setShowForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.img) {
      alert('Title and Image URL are required.')
      return
    }
    
    setFormLoading(true)
    setError('')
    try {
      if (editingId) {
        await updateProperty(editingId, formData)
      } else {
        await addProperty(formData)
      }
      resetForm()
      await fetchProperties()
    } catch (err) {
      setError('Failed to save property. ' + err.message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to soft-delete "${title}"?`)) {
      try {
        await deleteProperty(id, false)
        await fetchProperties()
      } catch (err) {
        setError('Failed to delete property. ' + err.message)
      }
    }
  }

  const handleRestore = async (id) => {
    try {
      await restoreProperty(id)
      await fetchProperties()
    } catch (err) {
      setError('Failed to restore property. ' + err.message)
    }
  }

  if (loading) return <div>Loading properties...</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.2rem', color: '#7a8fa6', margin: 0 }}>Manage Properties ({properties.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true) }} className="btn-gold" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
          + Add Property
        </button>
      </div>

      {error && <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: 12, borderRadius: 8, marginBottom: 20 }}>{error} <button onClick={fetchProperties} style={{ background: 'transparent', border: '1px solid currentColor', color: 'inherit', padding: '2px 8px', borderRadius: 4, marginLeft: 10, cursor: 'pointer' }}>Retry</button></div>}

      {showForm && (
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: 24, borderRadius: 12, marginBottom: 30 }}>
          <h3 style={{ marginTop: 0, marginBottom: 20 }}>{editingId ? 'Edit Property' : 'New Property'}</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <input type="text" placeholder="Title *" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
              <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} style={inputStyle}>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="industrial">Industrial</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
              <input type="url" placeholder="Image URL *" value={formData.img} onChange={e => setFormData({...formData, img: e.target.value})} required style={inputStyle} />
              <input type="text" placeholder="Badge/Label (e.g. For Sale, New Launch)" value={formData.badge} onChange={e => setFormData({...formData, badge: e.target.value})} style={inputStyle} />
            </div>
            
            {formData.img && (
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', color: '#7a8fa6', display: 'block', marginBottom: 5 }}>Image Preview:</span>
                <img src={formData.img} alt="Preview" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image+URL' }} style={{ height: 100, objectFit: 'cover', borderRadius: 8 }} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <input type="text" placeholder="Location (e.g. Haryana)" value={formData.loc} onChange={e => setFormData({...formData, loc: e.target.value})} style={inputStyle} />
              <input type="text" placeholder="Area (e.g. 100-500 Sq. Yards)" value={formData.area} onChange={e => setFormData({...formData, area: e.target.value})} style={inputStyle} />
              <input type="text" placeholder="Legal/Extra (e.g. Clear Title)" value={formData.legal} onChange={e => setFormData({...formData, legal: e.target.value})} style={inputStyle} />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <button type="submit" disabled={formLoading} className="btn-gold" style={{ padding: '10px 24px' }}>
                {formLoading ? 'Saving...' : 'Save Property'}
              </button>
              <button type="button" onClick={resetForm} disabled={formLoading} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '10px 24px', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {properties.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: 40, borderRadius: 12, textAlign: 'center', color: '#7a8fa6' }}>
          No properties found.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {properties.map(p => (
            <div key={p.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, overflow: 'hidden', border: p.isDeleted ? '1px dashed #ef4444' : '1px solid transparent', opacity: p.isDeleted ? 0.7 : 1 }}>
              <img src={p.img} alt={p.title} onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error' }} style={{ width: '100%', height: 160, objectFit: 'cover' }} />
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{p.title}</h4>
                  <span style={{ fontSize: '0.75rem', background: 'rgba(245, 197, 24, 0.2)', color: '#f5c518', padding: '2px 8px', borderRadius: 10 }}>{p.badge}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#7a8fa6', marginBottom: 12 }}>{p.type.charAt(0).toUpperCase() + p.type.slice(1)} | {p.loc || 'No location'}</div>
                {p.isDeleted && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: 10 }}>[DELETED - TRASH]</div>}
                
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', marginBottom: 16 }}>
                  <div><i className="fas fa-ruler-combined" style={{marginRight:5}} /> {p.area}</div>
                  <div style={{marginTop: 5}}><i className="fas fa-file-contract" style={{marginRight:5}} /> {p.legal}</div>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleEdit(p)} style={actionBtnStyle}>Edit</button>
                  {p.isDeleted ? (
                    <button onClick={() => handleRestore(p.id)} style={{...actionBtnStyle, color: '#4ade80', borderColor: 'rgba(34,197,94,0.3)'}}>Restore</button>
                  ) : (
                    <button onClick={() => handleDelete(p.id, p.title)} style={{...actionBtnStyle, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)'}}>Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: 'white', fontFamily: "'Inter', sans-serif"
}

const actionBtnStyle = {
  flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 600
}
